import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { reportIssueApi } from '@/lib/api/report-issue.api'
import {
  CreateReportIssueRequest,
  GetReportIssuesRequest,
} from '@/lib/types/report-issue.types'
import { toast } from '@/hooks/use-toast'

/**
 * Hook to fetch all report issues with optional filters and pagination
 */
export function useReportIssues(params?: GetReportIssuesRequest) {
  return useQuery({
    queryKey: ['report-issues', params],
    queryFn: () => reportIssueApi.getAll(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

/**
 * Hook to fetch a single report issue by ID
 */
export function useReportIssue(issueId: string) {
  return useQuery({
    queryKey: ['report-issues', issueId],
    queryFn: () => reportIssueApi.getById(issueId),
    enabled: !!issueId, // Only fetch if issueId is provided
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

/**
 * Hook to create a new report issue
 * Note: onSuccess callback is removed to allow components to handle success with dialog
 * Components should use the mutation's onSuccess callback to show ResponseDialog
 */
export function useCreateReportIssue() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateReportIssueRequest) => reportIssueApi.create(data),
    onSuccess: () => {
      // Invalidate any report issues queries if they exist
      queryClient.invalidateQueries({ queryKey: ['report-issues'] })

      // Note: Success toast/dialog is now handled by the component
      // This allows components to show ResponseDialog with issue_id
    },
    onError: (error: any) => {
      // Show error toast for failures
      const errorMessage =
        error?.response?.data?.message?.description ||
        error?.message ||
        'Failed to submit issue. Please try again.'

      toast({
        title: error?.response?.data?.message?.title || 'Submission Failed',
        description: errorMessage,
        variant: 'destructive',
      })
    },
  })
}